import { useCallback, useMemo, useReducer, useRef } from 'react';
import { useMountedState } from 'react-use';
import { get } from 'lodash';

import { ValidationError, ForbiddenError } from '@mssgme/api-client';
import { useWarning } from './useRestrictions';

let lastRequestId = 0;

const defaultState = {
    pending: false,
    fulfilled: false,
    rejected: false,
    error: null,
    result: null,
};

const initialState = {
    ...defaultState,
    requestId: -1,
    requestCount: 0,
};

function reducer(state, { type, requestId, ...rest }) {
    if (state.requestId > requestId) {
        return state;
    }

    switch (type) {
        case 'pending':
            return { ...state, ...defaultState, requestId, pending: true, requestCount: state.requestCount + 1 };
        case 'fulfilled':
            return { ...state, ...defaultState, requestId, fulfilled: true, result: rest.result };
        case 'rejected':
            return { ...state, ...defaultState, requestId, rejected: true, error: rest.error };
        default:
            throw new Error();
    }
}

/**
 * @template {*} R
 * @template {function(): Promise<R>} T
 * @param {T} fn Traced callback
 * @param {*[]} deps Callback dependencies
 * @returns {{
 *  pending: true,
 *  run: T
 * } | {
 *  fulfilled: true,
 *  result: R,
 *  run: T
 * } | {
 *  rejected: true,
 *  error: Error,
 *  validationErrors: *,
 *  getValidationError: function(string): *,
 *  run: T
 * }} Fetcher descriptor
 */
export function useFetching(fn, deps = []) {
    const ref = useRef(fn);
    const isMounted = useMountedState();
    const [state, dispatch] = useReducer(reducer, initialState);
    const { show } = useWarning();

    ref.current = fn;

    const run = useCallback(async (...args) => {
        function safeDispatch(...args) {
            if (isMounted()) {
                dispatch(...args);
            }
        }

        const requestId = lastRequestId++;

        safeDispatch({ type: 'pending', requestId });

        try {
            const result = await ref.current(...args);
            safeDispatch({ type: 'fulfilled', requestId, result });
        } catch (error) {
            safeDispatch({ type: 'rejected', requestId, error });

            // can be handled explicitly in business logic, otherwise handle automatically
            if (ForbiddenError.isPermissionBlock(error)) {
                show(error);
            }
        }
    }, deps);

    return useMemo(() => {
        const validationErrors = ValidationError.is(state.error) ? state.error.errors : {};
        const getValidationError = (name) => get(validationErrors, name);

        return {
            ...state,
            validationErrors,
            getValidationError,
            run,
        };
    }, [state, run]);
}
